import { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FormGroup,
  Modal,
  ModalBody,
  Form,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Button from "./../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import DateTimePicker from "../../../components/DateTimePicker/DateTimePicker";
import { Block } from "./../../../components/Block/Block";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "./../../../components/DataTable/DataTable";
import { Col, Row } from "./../../../components/Grid/Grid";
import { convertDate } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { passengerUserContext } from "../../../context/UserContext/PassengerUserContext/PassengerUserContext";
import { createPassengerValidation } from "../../../utils/validation/validation";
import PageHeader from "../../../components/PageHeader/PageHeader";
import TableFilter from "../../../components/TableFilter/TableFilter";
import SearchTable from "../../../components/SearchTable/SearchTable";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import UserInfoItem from "../../../components/UserInfoItem/UserInfoItem";
import { convertGenderTypeToText, genderTypeItems } from "../../../utils/enum";

const PassengersUser = () => {
  const loader = useSelector((state) => state.loader);

  const {
    passengers,
    id,
    filter,
    setFilter,
    sort,
    setSort,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetPassengers,
    handleCreatePassenger,
    handleEditPassenger,
    handleDeletePassenger,
  } = useContext(passengerUserContext);

  useEffect(() => {
    handleGetPassengers();
  }, [sort]);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createPassengerValidation),
  });

  return (
    <>
      <SiteTitle title="مسافران" />

      <PageHeader title="مسافران" />

      <TableFilter setSort={setSort} sort={sort} />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            firstItem={
              <Button
                color="primary"
                className="btn-icon"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon name="plus" />
              </Button>
            }
            filterPlaceholder="نام، نام خانوادگی، شماره موبایل، کد ملی و..."
            filter={filter}
            setFilter={setFilter}
            onSearch={handleGetPassengers}
          />
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">نام و نام خانوادگی</span>
              </DataTableRow>
              <DataTableRow size="mb">
                <span className="sub-text">کد ملی</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">شماره موبایل</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">تاریخ تولد</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">جنسیت</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {passengers.length > 0
              ? passengers.map((item) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.avatar
                              ? { image: item?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item.firstName} ${item.lastName}`}
                            subTitle={`${item.firstNameLatin} ${item.lastNameLatin}`}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="mb">
                        <span>{item.nationalNumber}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>{item.phoneNumber}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>{convertDate(item.birthDate)}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>{convertGenderTypeToText(item.gender)}</span>
                      </DataTableRow>

                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h" />
                              </DropdownToggle>
                              <DropdownMenu
                                left
                                className="dropdown_max_height"
                              >
                                <ul className="link-list-opt no-bdr text-right">
                                  <li
                                    onClick={() =>
                                      handleShowModal(item, setValue)
                                    }
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="edit-alt-fill" />
                                      <span>ویرایش</span>
                                    </DropdownItem>
                                  </li>
                                  <li className="divider" />
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleDeletePassenger(item._id),
                                        `آیا از حذف مسافر ${item.firstName} ${item.lastName} مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className="text-danger"
                                    >
                                      <Icon name="trash-fill" />
                                      <span>حذف</span>
                                    </DropdownItem>
                                  </li>
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          <div className="card-inner">
            {passengers.length <= 0 && (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      <Modal
        isOpen={isShowModal}
        toggle={() => handleHideModal()}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span
            type="button"
            onClick={() => handleHideModal()}
            className="close"
          >
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">
              {id ? "ویرایش اطلاعات مسافر" : "افزودن مسافر جدید"}
            </h5>
            <br />
            <Form
              autoComplete="off"
              className="is-alter"
              onSubmit={handleSubmit(
                id ? handleEditPassenger : handleCreatePassenger
              )}
            >
              <Row className="gy-4">
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="firstName">
                      نام فارسی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام فارسی خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("firstName")}
                      />
                      <ErrorMessage message={errors.firstName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="lastName">
                      نام خانوادگی فارسی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام خانوادگی فارسی خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("lastName")}
                      />
                      <ErrorMessage message={errors.lastName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="firstNameLatin">
                      نام لاتین
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام لاتین خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("firstNameLatin")}
                      />
                      <ErrorMessage message={errors.firstNameLatin?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="lastNameLatin">
                      نام خانوادگی لاتین
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام خانوادگی لاتین خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("lastNameLatin")}
                      />
                      <ErrorMessage message={errors.lastNameLatin?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="nationalNumber">
                      کدملی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="کدملی خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("nationalNumber")}
                      />
                      <ErrorMessage message={errors.nationalNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="phoneNumber">
                      شماره موبایل
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="شماره موبایل خود را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("phoneNumber")}
                      />
                      <ErrorMessage message={errors.phoneNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="gender">
                      جنسیت
                    </label>
                    <div className="form-control-wrap">
                      <select
                        className="form-control-lg form-control"
                        {...register("gender")}
                      >
                        <option value="" selected hidden>
                          جنسیت مسافر را انتخاب کنید
                        </option>
                        {genderTypeItems.map((item) => (
                          <option
                            key={item}
                            value={item}
                            selected={watch("gender") === item}
                          >
                            {convertGenderTypeToText(item)}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage message={errors.gender?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="birthDate">
                      تاریخ تولد
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateTimePicker
                            type="single"
                            placeholder="تاریخ تولد را وارد نمایید"
                            initValue={value}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                        name="birthDate"
                      />

                      <ErrorMessage message={errors.birthDate?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        {id ? "ویرایش" : "افزودن"} مسافر
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => handleHideModal()}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PassengersUser;
